import { combineReducers } from "redux";
import * as Actions from "./actions";
import analysisSetReducer from "./AnalysisSetReducer";
import { AnalyseCoreState, LoadingStateEnum, PlayStateEnum } from "./interfaces/state";
import { FigureType, PeaksInstance, Point, Segment } from "peaks.js";

declare global {
    interface Window {
        _peaksJs: PeaksInstance;
    }
}

export function coreReducer(
    state: AnalyseCoreState = null,
    action: Actions.Action
): AnalyseCoreState {
    switch (action.type) {
        case Actions.ActionType.RequestCoughAnalysisSets:
            return {
                ...state,
                loadingState: LoadingStateEnum.AnalysisSets,
            };
        case Actions.ActionType.ReceiveSessionMetadata:
            return {
                ...state,
                sessionMetadata: action.metadata
            };
        case Actions.ActionType.SetStudyId:
            return {
                ...state,
                studyId: action.studyId
            };
        case Actions.ActionType.InitializePeaks:
            // For automated testing purposes, it is useful to have
            // The PeaksJS object available globally to test for correct state.
            if (window) {
                window._peaksJs = action.peaksJs;
            }

            return {
                ...state,
                peaksJs: action.peaksJs
            };
        case Actions.ActionType.DestoryPeaks:

            if (window) {
                window._peaksJs = null;
            }

            return {
                ...state,
                peaksJs: null
            };

        case Actions.ActionType.RequestEvents:
            return {
                ...state,
                loadingState: LoadingStateEnum.Events,
            };
        case Actions.ActionType.LoadPositions:
            return {
                ...state,
                positions: action.positions,
            };

        case Actions.ActionType.LoadingPositions:
            return {
                ...state,
                loadingState: LoadingStateEnum.Positions,
            };
        case Actions.ActionType.ReceiveEvents:
            return {
                ...state
            };
        case Actions.ActionType.RequestAnalysisSetComments:
            return {
                ...state,
                comments: action.comments,
            }
        case Actions.ActionType.RequestCurrentAnalysisSet:
            return {
                ...state,
                coughAnalysisSet: action.analysisSet,
            }
        case Actions.ActionType.RequestVisualReviewForAnalysisSet:
            return {
                ...state,
                requiresVisualReview: action.visualReview,
            }
        case Actions.ActionType.RequestBlindComments:
            return {
                ...state,
                blindComments: action.blindComments,
            }
        case Actions.ActionType.RequestEventTypes:
            return {
                ...state,
                loadingState: LoadingStateEnum.EventTypes
            };
        case Actions.ActionType.ReceiveEventTypes:
            return {
                ...state,
                availableEventTypes: action.eventTypes,
                selectedEventType: state.selectedEventType === undefined ? action.eventTypes[0] : state.selectedEventType
            };
        case Actions.ActionType.Play:
            return {
                ...state,
                playState: PlayStateEnum.Playing
            };
        case Actions.ActionType.Pause:
            return {
                ...state,
                playState: PlayStateEnum.Paused
            };
        case Actions.ActionType.ToggleMuteMicrophone:
            return {
                ...state,
                isMicrophoneMuted: !state.isMicrophoneMuted
            };
        case Actions.ActionType.ToggleMuteSensor:
            return {
                ...state,
                isSensorMuted: !state.isSensorMuted
            };
        case Actions.ActionType.ToggleGridline:
            return {
                ...state,
                isGridlineVisible: !state.isGridlineVisible
            };
        case Actions.ActionType.ToggleScrollAction:
            return {
                ...state,
                isScrollActionZoom: !state.isScrollActionZoom
            };
        case Actions.ActionType.SetZoomLevel:
            return {
                ...state,
                zoomLevel: action.level
            };
        case Actions.ActionType.SetWaveform:
            return {
                ...state,
                selectedWaveform: action.waveform
            };
        case Actions.ActionType.ChangeWaveform:
            return {
                ...state,
                previouslySelectedWaveform: action.currentWaveform,
                selectedWaveform: action.waveformToChangeTo,
                previousPlaybackTimepoint: action.currentTime,
                isMicrophoneMuted: false,
                isSensorMuted: false,
            };
        case Actions.ActionType.ChangeSelectedEvent:
            return {
                ...state,
                selectedEventType: action.eventType
            };
        case Actions.ActionType.ChangeSelectedSegment:
            return {
                ...state,
                selectedSegment: action.segment
            };
        case Actions.ActionType.LoadError:
            return {
                ...state,
                isSubmitting: false,
                isLoadError: true
            };
        case Actions.ActionType.HourlySegmentsLoadError:
            return {
                ...state,
                configErrorMessage: "Failed to load Hour Segments List, this feature has been disabled",
                loadingState: LoadingStateEnum.Loaded,
            };
        case Actions.ActionType.OpenFixedCommentsDialog:
            return {
                ...state,
                isFixedCommentsDialogOpen: true
            };
        case Actions.ActionType.CloseFixedCommentsDialog:
            return {
                ...state,
                isFixedCommentsDialogOpen: false
            };
        case Actions.ActionType.ToggleKeyboardShortcuts:
            return {
                ...state,
                areKeyboardShortcutsVisible: !state.areKeyboardShortcutsVisible
            };
        case Actions.ActionType.RequestFixedComments:
            return {
                ...state,
                loadingState: LoadingStateEnum.SessionComments
            };
        case Actions.ActionType.ReceiveFixedComments:
            return {
                ...state,
                availableFixedComments: action.comments.map(comment => ({
                    ...comment,
                    IsSelected: false
                }))
            };
        case Actions.ActionType.UpdateViewOnlyState:
            return {
                ...state,
                isViewOnly: action.isViewOnly
            };
        case Actions.ActionType.UpdateAddEventByDoubleClickState:
            return {
                ...state,
                canAddEventByDoubleClick: action.canAddEventByDoubleClick
            };
        case Actions.ActionType.ResumePlaybackJumpComplete:
            return {
                ...state,
                storedPlaybackTime: null
            };
        case Actions.ActionType.OpenEventListDialog:
            return {
                ...state,
                isEventListOpen: true
            };
        case Actions.ActionType.CloseEventListDialog:
            return {
                ...state,
                isEventListOpen: false
            };
        case Actions.ActionType.OpenDisputedListDialog:
            return {
                ...state,
                isDisputedListOpen: true
            };
        case Actions.ActionType.CloseDisputedListDialog:
            return {
                ...state,
                isDisputedListOpen: false
            };
        case Actions.ActionType.CloseSendForVisualReviewDialog:
            return {
                ...state,
                isSendForReviewModalOpen: false
            };
        case Actions.ActionType.ReceiveNotes:
            return {
                ...state,
                sessionNotes: action.notes
            };
        case Actions.ActionType.ToggleSessionNotesVisibility:
            return {
                ...state,
                isSessionNotesVisible: !state.isSessionNotesVisible
            };
        case Actions.ActionType.AddingNewNote:
            return {
                ...state,
                isAddingSessionNote: true
            };
        case Actions.ActionType.AddNewNoteDone:
            return {
                ...state,
                isAddingSessionNote: false
            };
        case Actions.ActionType.PlayheadMapped:
            return {
                ...state,
                loadingState: LoadingStateEnum.Loaded
            };
        case Actions.ActionType.OpenHourSegmentsModal:
            return {
                ...state,
                isHourSegmentsModalOpened: true
            };
        case Actions.ActionType.CloseHourSegmentsModal:
            return {
                ...state,
                isHourSegmentsModalOpened: false
            };
        case Actions.ActionType.LoadHourSegments:
            const kvp = action.hourSegmentsMap.entries().next().value;
            return {
                ...state,
                hourSegmentsMap: state.hourSegmentsMap.set(kvp[0], kvp[1])
            }
        case Actions.ActionType.RequestHourSegments:
            return {
                ...state,
                loadingState: LoadingStateEnum.HourSegments
            };
        case Actions.ActionType.ShowAcceptabilityAuditModal:
            return {
                ...state,
                isAcceptabilityAuditModalVisible: true,
                acceptabilityStatus: action.acceptability,
            };
        case Actions.ActionType.CloseAcceptabilityAuditModal:
            return {
                ...state,
                isAcceptabilityAuditModalVisible: false,
                acceptabilityStatus: null,
            };
        case Actions.ActionType.AcceptabilitySetIsSubmitting:
            return {
                ...state,
                isSubmitting: action.submitting,
            };
        case Actions.ActionType.PopulateRawTimepoints:
            return {
                ...state,
                rawTimestampPoints: action.rawTimestampPoints,
            };
        case Actions.ActionType.PlaybackServiceStatusAction:
            return {
                ...state,
                playbackServiceStatus: action.status,
            };
        case Actions.ActionType.IsSegmented:
            return {
                ...state,
                isSegmented: action.isSegmented,
            };
        case Actions.ActionType.EventTypesNotConfigured:
            return {
                ...state,
                isViewOnly: true,
                configErrorMessage: "Failed to load event types, please contact the administrator to configure these for the study",
                loadingState: LoadingStateEnum.Loaded,
            };
        case Actions.ActionType.SetEventDuplicateThreshold:
            return {
                ...state,
                eventDuplicateThreshold: action.threshold
            };
        case Actions.ActionType.SetAcceptanceError:
            return {
                ...state,
                acceptanceErrorMessage: action.message
            };
        case Actions.ActionType.SetDisputedAcceptanceError:
            return {
                ...state,
                disputedAcceptanceErrorMessage: action.message
            };
        case Actions.ActionType.SetDuplicateVerified: 
            return {
                ...state,
                confirmedDuplicatePoints: new Set([
                    ...state.confirmedDuplicatePoints,
                    action.id
                ]),
                acceptanceErrorMessage: null
            };
        case Actions.ActionType.SetCoughSettings:
            return {
                ...state,
                adjudicationEnabled: action.payload
            };
        case Actions.ActionType.SetVisualInspectionErrorAction:
            return {
                ...state,
                visualInspectionErrorMessage: action.message
            }
        case Actions.ActionType.SetAudioLength:
            return {
                ...state,
                audioLength: action.length
            }
        case Actions.ActionType.ToggleQCStatements:
            return {
                ...state,
                isQCStatementsOpen: action.isOpen
            }
        default:
            return state;
    }
}

export default combineReducers({
    core: coreReducer,
    analysisSet: analysisSetReducer
});
